import { ComponentType, lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
} from "react-router-dom";

import { AiFeatureRoute } from "./components/auth/ai-feature-route";
import { OrganizationTypeRoute } from "./components/auth/organization-type-route";
import { RoleRoute } from "./components/auth/role-route";
import { AuthRoute } from "./components/auth/route";
import { ErrorPage } from "./components/error/error-page";
import LoadingScreen from "./components/loading";
import RootLayout from "./layouts/root";
import { ManageChannels } from "./pages/channels/manage";
import { ChannelRedirect } from "./pages/channels/manage/channel-redirect";
import VariantAttributesPage from "./pages/ecommerce/variant-attributes";
import { BootstrapProvider } from "./providers/bootstrap";
import { ORGANIZATION_TYPE } from "./types/organization";
import { isMockEnabled } from "./mock/config";

import { TutorialPageSkeleton } from "@/components/tutorials/tutorial-page-skeleton";
import {
  AI_ASSISTANT,
  assistantPath,
  isTutorialsEnabled,
} from "@/config/constants";
import { SuppliesCartRedirect } from "@/features/supplies/components/cart/cart-redirect";
import { SuppliesFeatureRoute } from "@/features/supplies/components/shared/supplies-feature-route";
import { AI_FEATURE } from "@/types/workspace";

const lazyWithRetry = (
  importFn: () => Promise<{ default: ComponentType<unknown> }>,
) =>
  lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      // One retry handles stale chunks after a deploy/HMR rebuild.
      // If it still fails, propagate so react-router's errorElement renders.
      try {
        return await importFn();
      } catch {
        throw error;
      }
    }
  });

const AppLayout = lazyWithRetry(() =>
  import("./layouts/app").then((module) => ({ default: module.AppLayout })),
);
const IndexPage = lazyWithRetry(() => import("./pages"));
const MockSettingsPage = lazyWithRetry(() => import("./pages/mock-settings"));
const InboxPage = lazyWithRetry(() =>
  import("./pages/inbox/[workspaceId]/[contactId]").then((module) => ({
    default: module.InboxPage,
  })),
);
const DashboardPage = lazyWithRetry(() =>
  import("./pages/dashboard").then((module) => ({
    default: module.DashboardPage,
  })),
);
const AcceptInvitePage = lazyWithRetry(() =>
  import("./pages/auth/accept-invite").then((module) => ({
    default: module.AcceptInvitePage,
  })),
);
const ForgotPasswordPage = lazyWithRetry(() =>
  import("./pages/auth/forgot-password").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const OnboardingPage = lazyWithRetry(() =>
  import("./pages/auth/onboarding").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const SignInPage = lazyWithRetry(() =>
  import("./pages/auth/sign-in").then((module) => ({
    default: module.SignInPage,
  })),
);
const SignOutPage = lazyWithRetry(() =>
  import("./pages/auth/sign-out").then((module) => ({
    default: module.SignOutPage,
  })),
);
const SignUpPage = lazyWithRetry(() =>
  import("./pages/auth/sign-up").then((module) => ({
    default: module.SignUpPage,
  })),
);
const SSOCallbackPage = lazyWithRetry(() =>
  import("./pages/auth/sso-callback").then((module) => ({
    default: module.SSOCallbackPage,
  })),
);
const JoinOrganizationPage = lazyWithRetry(() =>
  import("./pages/auth/join-organization").then((module) => ({
    default: module.JoinOrganizationPage,
  })),
);
const AcceptInvitationPage = lazyWithRetry(() =>
  import("./pages/auth/accept-invitation").then((module) => ({
    default: module.AcceptInvitationPage,
  })),
);
const ContactsPage = lazyWithRetry(() =>
  import("./pages/contacts").then((module) => ({
    default: module.ContactsPage,
  })),
);
const ContactDetailPage = lazyWithRetry(() =>
  import("./pages/contact-detail").then((module) => ({
    default: module.ContactDetailPage,
  })),
);
const TicketsPage = lazyWithRetry(() =>
  import("./pages/tickets").then((module) => ({
    default: module.TicketsPage,
  })),
);
const BroadcastsPage = lazyWithRetry(() =>
  import("./pages/broadcasts").then((module) => ({
    default: module.BroadcastsPage,
  })),
);
const BroadcastCreatePage = lazyWithRetry(() =>
  import("./pages/broadcasts/create").then((module) => ({
    default: module.BroadcastCreatePage,
  })),
);
const BroadcastDetailPage = lazyWithRetry(() =>
  import("./pages/broadcasts/[id]").then((module) => ({
    default: module.BroadcastDetailPage,
  })),
);
const BroadcastEditPage = lazyWithRetry(() =>
  import("./pages/broadcasts/[id]/edit").then((module) => ({
    default: module.BroadcastEditPage,
  })),
);

const WorkflowsPage = lazyWithRetry(() =>
  import("./pages/workflows").then((module) => ({
    default: module.WorkflowsPage,
  })),
);
const WorkflowCreatePage = lazyWithRetry(() =>
  import("./pages/workflows/create").then((module) => ({
    default: module.WorkflowCreatePage,
  })),
);
const WorkflowEditPage = lazyWithRetry(() =>
  import("./pages/workflows/[id]").then((module) => ({
    default: module.WorkflowEditPage,
  })),
);
const WorkflowActivitiesPage = lazyWithRetry(() =>
  import("./pages/workflows/[id]/activities").then((module) => ({
    default: module.WorkflowActivitiesPage,
  })),
);

const AdsPage = lazyWithRetry(() =>
  import("./pages/ads").then((module) => ({
    default: module.AdsPage,
  })),
);

const ReportsLayout = lazyWithRetry(() =>
  import("./pages/reports").then((module) => ({
    default: module.ReportsPage,
  })),
);
const ReportExportDownloadPage = lazyWithRetry(() =>
  import("./pages/reports/export-download").then((module) => ({
    default: module.ReportExportDownloadPage,
  })),
);
const ReportsLandingPage = lazyWithRetry(() =>
  import("./pages/reports/landing").then((module) => ({
    default: module.ReportsLandingPage,
  })),
);
const AiReportsPage = lazyWithRetry(() =>
  import("./pages/reports/ai").then((module) => ({
    default: module.default,
  })),
);
const AppointmentReportsPage = lazyWithRetry(() =>
  import("./pages/reports/appointments").then((module) => ({
    default: module.default,
  })),
);
const AttendanceReportsPage = lazyWithRetry(() =>
  import("./pages/reports/attendance").then((module) => ({
    default: module.AttendanceReportsPage,
  })),
);
const DoctorsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/doctors").then((module) => ({
    default: module.DoctorsReportsPage,
  })),
);
const CollectionsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/collections").then((module) => ({
    default: module.CollectionsReportsPage,
  })),
);
const DailyOperationsPage = lazyWithRetry(() =>
  import("./pages/reports/daily-operations").then((module) => ({
    default: module.default,
  })),
);
const AgentReportsPage = lazyWithRetry(() =>
  import("./pages/reports/agents").then((module) => ({
    default: module.AgentReportsPage,
  })),
);
const CampaignsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/campaigns").then((module) => ({
    default: module.CampaignsReportsPage,
  })),
);
const ContactsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/contacts").then((module) => ({
    default: module.ContactsReportsPage,
  })),
);
const ConversationsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/conversations").then((module) => ({
    default: module.ConversationsReportsPage,
  })),
);
const DailyTrackerReportsPage = lazyWithRetry(() =>
  import("./pages/reports/daily-tracker").then((module) => ({
    default: module.DailyTrackerReportsPage,
  })),
);
const LeadsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/leads").then((module) => ({
    default: module.LeadsReportsPage,
  })),
);
const LostReportsPage = lazyWithRetry(() =>
  import("./pages/reports/lost").then((module) => ({
    default: module.LostReportsPage,
  })),
);
const SourcesReportsPage = lazyWithRetry(() =>
  import("./pages/reports/sources").then((module) => ({
    default: module.SourcesReportsPage,
  })),
);
const BroadcastsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/broadcasts").then((module) => ({
    default: module.BroadcastsReportsPage,
  })),
);
const LifecycleReportsPage = lazyWithRetry(() =>
  import("./pages/reports/lifecycle").then((module) => ({
    default: module.LifecycleReportsPage,
  })),
);
const FollowUpsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/follow-ups").then((module) => ({
    default: module.FollowUpsReportsPage,
  })),
);
const MessagesReportsPage = lazyWithRetry(() =>
  import("./pages/reports/messages").then((module) => ({
    default: module.MessagesReportsPage,
  })),
);
const HandoversReportPage = lazyWithRetry(() =>
  import("./pages/reports/handovers").then((module) => ({
    default: module.default,
  })),
);
const SalesProductsReportsPage = lazyWithRetry(() =>
  import("./pages/reports/sales-products").then((module) => ({
    default: module.SalesProductsReportsPage,
  })),
);
const SalesCustomersReportsPage = lazyWithRetry(() =>
  import("./pages/reports/sales-customers").then((module) => ({
    default: module.SalesCustomersReportsPage,
  })),
);
const SalesDeliveriesReportsPage = lazyWithRetry(() =>
  import("./pages/reports/sales-deliveries").then((module) => ({
    default: module.SalesDeliveriesReportsPage,
  })),
);
const SalesReportsPage = lazyWithRetry(() =>
  import("./pages/reports/sales").then((module) => ({
    default: module.SalesReportsPage,
  })),
);
const SettingsOverviewPage = lazyWithRetry(() =>
  import("./pages/settings").then((module) => ({
    default: module.default,
  })),
);
const TutorialsPage = lazyWithRetry(() => import("./pages/tutorials"));
const OrganizationPage = lazyWithRetry(() =>
  import("./pages/settings/organization").then((module) => ({
    default: module.default,
  })),
);
const SnippetsPage = lazyWithRetry(() =>
  import("./pages/settings/snippets").then((module) => ({
    default: module.default,
  })),
);
const FilesPage = lazyWithRetry(() =>
  import("./pages/settings/files").then((module) => ({
    default: module.default,
  })),
);
const TemplatesPage = lazyWithRetry(() => import("./pages/settings/templates"));
const TemplateEditorPage = lazyWithRetry(
  () => import("./pages/settings/templates/editor"),
);
const UsersPage = lazyWithRetry(() =>
  import("./pages/settings/users").then((module) => ({
    default: module.default,
  })),
);
const DeveloperPage = lazyWithRetry(() =>
  import("./pages/settings/developer").then((module) => ({
    default: module.default,
  })),
);
const SubscriptionPage = lazyWithRetry(() =>
  import("./pages/settings/subscription").then((module) => ({
    default: module.default,
  })),
);
const BillingPage = lazyWithRetry(() => import("./pages/billing"));
const BillingSuccessPage = lazyWithRetry(
  () => import("./pages/billing/success"),
);
const BillingCancelPage = lazyWithRetry(() => import("./pages/billing/cancel"));
const LifecyclePage = lazyWithRetry(() =>
  import("./pages/settings/lifecycle").then((module) => ({
    default: module.default,
  })),
);
const ClosingNotesPage = lazyWithRetry(() =>
  import("./pages/settings/closing-notes").then((module) => ({
    default: module.default,
  })),
);
const OutcomeReasonsPage = lazyWithRetry(() =>
  import("./pages/settings/outcome-reasons").then((module) => ({
    default: module.default,
  })),
);
const WrapUpPage = lazyWithRetry(() =>
  import("./pages/settings/wrap-up").then((module) => ({
    default: module.default,
  })),
);
const CreditsSettingsPage = lazyWithRetry(() =>
  import("./pages/settings/credits").then((module) => ({
    default: module.default,
  })),
);
const AiSettingsPage = lazyWithRetry(() =>
  import("./pages/settings/ai").then((module) => ({
    default: module.default,
  })),
);
const AssignmentPage = lazyWithRetry(() =>
  import("./pages/settings/assignment").then((module) => ({
    default: module.default,
  })),
);
const InboxSettingsPage = lazyWithRetry(() =>
  import("./pages/settings/inbox").then((module) => ({
    default: module.default,
  })),
);
const SlaSettingsPage = lazyWithRetry(() =>
  import("./pages/settings/sla").then((module) => ({
    default: module.default,
  })),
);
const TagsPage = lazyWithRetry(() =>
  import("./pages/settings/tags").then((module) => ({
    default: module.default,
  })),
);
const ContactFieldsPage = lazyWithRetry(() =>
  import("./pages/settings/contact-fields").then((module) => ({
    default: module.default,
  })),
);
const WebsitesPage = lazyWithRetry(() =>
  import("./pages/settings/websites").then((module) => ({
    default: module.default,
  })),
);
const WorkspaceSettingsPage = lazyWithRetry(() =>
  import("./pages/settings/workspace").then((module) => ({
    default: module.default,
  })),
);
const WorkspaceUsersPage = lazyWithRetry(() =>
  import("./pages/settings/workspace-users").then((module) => ({
    default: module.default,
  })),
);
const TeamsPage = lazyWithRetry(() =>
  import("./pages/settings/teams").then((module) => ({
    default: module.default,
  })),
);
const RolesPage = lazyWithRetry(() =>
  import("./pages/settings/roles").then((module) => ({
    default: module.default,
  })),
);
const RolePage = lazyWithRetry(() =>
  import("./pages/settings/roles/[roleId]").then((module) => ({
    default: module.default,
  })),
);
const AgentStudioPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]").then((module) => ({
    default: module.default,
  })),
);
const AgentQuickReplyPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/quick-reply").then((module) => ({
    default: module.default,
  })),
);
const AgentFilesPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/files").then((module) => ({
    default: module.default,
  })),
);
const AgentTestsPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/tests").then((module) => ({
    default: module.default,
  })),
);
const AgentRunsPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/runs").then((module) => ({
    default: module.default,
  })),
);
const AgentRunDetailPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/runs/[runId]").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const AgentAvailabilityPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/availability").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const AgentKnowledgeBasePage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/knowledge-base").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const AgentSuggestionsPage = lazyWithRetry(() =>
  import("./pages/settings/ai-agents/[agentId]/suggestions").then((module) => ({
    default: module.default,
  })),
);
const BusinessHoursPage = lazyWithRetry(() =>
  import("./pages/settings/business-hours").then((module) => ({
    default: module.default,
  })),
);
const ProfilePage = lazyWithRetry(() => import("./pages/profile"));
const NotificationsPage = lazyWithRetry(() => import("./pages/notifications"));
const ChannelsPage = lazyWithRetry(() =>
  import("./pages/channels/index").then((module) => ({
    default: module.ChannelsPage,
  })),
);
const CreateChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/index").then((module) => ({
    default: module.CreateChannelPage,
  })),
);
const CreateInstagramChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/instagram").then((module) => ({
    default: module.CreateInstagramChannelPage,
  })),
);
const CreateWhatsAppChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/whatsapp").then((module) => ({
    default: module.CreateWhatsAppChannelPage,
  })),
);
const CreateWhatsAppCoexistencePage = lazyWithRetry(() =>
  import("./pages/channels/create/whatsapp-coexistence").then((module) => ({
    default: module.CreateWhatsAppCoexistencePage,
  })),
);
const CreateMessengerChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/messenger").then((module) => ({
    default: module.CreateMessengerChannelPage,
  })),
);
const CreateTikTokChannelPage = lazyWithRetry(() =>
  import("./pages/channels/create/tiktok").then((module) => ({
    default: module.CreateTikTokChannelPage,
  })),
);
const ChannelConfiguration = lazyWithRetry(() =>
  import("./pages/channels/manage/configuration").then((module) => ({
    default: module.ChannelConfiguration,
  })),
);
const OAuthCallbackHandler = lazyWithRetry(() =>
  import("./pages/channels/manage/oauth-callback").then((module) => ({
    default: module.OAuthCallbackHandler,
  })),
);
const WhatsappTemplatesRedirect = lazyWithRetry(
  () => import("./pages/channels/manage/whatsapp/templates/edit"),
);
const ChannelPostsPage = lazyWithRetry(
  () => import("./pages/channels/manage/posts"),
);
const ChannelProfilePage = lazyWithRetry(
  () => import("./pages/channels/manage/profile"),
);
const WelcomeMessagesPage = lazyWithRetry(
  () => import("./pages/channels/manage/welcome-messages"),
);
const WelcomeMessagesEditPage = lazyWithRetry(
  () => import("./pages/channels/manage/welcome-messages/edit"),
);
const PersistentMenuPage = lazyWithRetry(
  () => import("./pages/channels/manage/persistent-menu"),
);
const EcommercePage = lazyWithRetry(() =>
  import("./pages/ecommerce").then((module) => ({
    default: module.EcommercePage,
  })),
);
const ProductsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/products").then((module) => ({
    default: module.ProductsPage,
  })),
);
const OrdersPage = lazyWithRetry(() =>
  import("./pages/ecommerce/orders").then((module) => ({
    default: module.OrdersPage,
  })),
);
const ProductCreatePage = lazyWithRetry(() =>
  import("./pages/ecommerce/products/create").then((module) => ({
    default: module.ProductCreatePage,
  })),
);
const ProductEditPage = lazyWithRetry(() =>
  import("./pages/ecommerce/products/edit").then((module) => ({
    default: module.ProductEditPage,
  })),
);
const ProductVariantsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/products/variants").then((module) => ({
    default: module.ProductVariantsPage,
  })),
);
const CategoriesPage = lazyWithRetry(() =>
  import("./pages/ecommerce/categories").then((module) => ({
    default: module.CategoriesPage,
  })),
);
const BrandsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/brands").then((module) => ({
    default: module.BrandsPage,
  })),
);
const DiscountsPage = lazyWithRetry(() =>
  import("./pages/ecommerce/discounts").then((module) => ({
    default: module.DiscountsPage,
  })),
);
const StorePage = lazyWithRetry(() =>
  import("./pages/ecommerce/store").then((module) => ({
    default: module.StorePage,
  })),
);
const StoreBuilderPage = lazyWithRetry(() =>
  import("./pages/ecommerce/store/builder").then((module) => ({
    default: module.StoreBuilderPage,
  })),
);

const IntegrationsPage = lazyWithRetry(() =>
  import("./pages/integrations/index").then((module) => ({
    default: module.IntegrationsPage,
  })),
);

const DoctorsPage = lazyWithRetry(() =>
  import("./pages/clinics/doctors").then((module) => ({
    default: module.DoctorsPage,
  })),
);
const TreatmentDetailPage = lazyWithRetry(() =>
  import("./pages/clinics/treatments/[treatmentId]").then((module) => ({
    default: module.default,
  })),
);
const DoctorDetailPage = lazyWithRetry(() =>
  import("./pages/clinics/doctors/[doctorId]").then((module) => ({
    default: module.default,
  })),
);
const DoctorNewPage = lazyWithRetry(() =>
  import("./pages/clinics/doctors/new").then((module) => ({
    default: module.default,
  })),
);
const TreatmentNewPage = lazyWithRetry(() =>
  import("./pages/clinics/treatments/new").then((module) => ({
    default: module.default,
  })),
);
const TreatmentsPage = lazyWithRetry(() =>
  import("./pages/clinics/treatments").then((module) => ({
    default: module.TreatmentsPage,
  })),
);
const AppointmentsPage = lazyWithRetry(() =>
  import("./pages/clinics/appointments").then((module) => ({
    default: module.AppointmentsPage,
  })),
);
const ClinicBillingPage = lazyWithRetry(() =>
  import("./pages/clinics/billing").then((module) => ({
    default: module.ClinicBillingPage,
  })),
);
const DepartmentsPage = lazyWithRetry(() =>
  import("./pages/clinics/departments").then((module) => ({
    default: module.DepartmentsPage,
  })),
);

const SuppliesLayout = lazyWithRetry(() =>
  import("./features/supplies/components/shared/supplies-layout").then(
    (module) => ({ default: module.SuppliesLayout }),
  ),
);
const SuppliesInventoryPage = lazyWithRetry(
  () => import("./pages/supplies/inventory"),
);
const SuppliesInventoryItemPage = lazyWithRetry(
  () => import("./pages/supplies/inventory/[inventoryId]"),
);
const SuppliesCataloguePage = lazyWithRetry(
  () => import("./pages/supplies/catalogue"),
);
const SuppliesProductPage = lazyWithRetry(
  () => import("./pages/supplies/catalogue/[productId]"),
);
const SuppliesCheckoutPage = lazyWithRetry(
  () => import("./pages/supplies/checkout"),
);
const SuppliesOrdersPage = lazyWithRetry(
  () => import("./pages/supplies/orders"),
);
const SuppliesOrderPage = lazyWithRetry(
  () => import("./pages/supplies/orders/[orderId]"),
);
const SuppliesOrderRequestsPage = lazyWithRetry(
  () => import("./pages/supplies/order-requests"),
);
const SuppliesOrderRequestPage = lazyWithRetry(
  () => import("./pages/supplies/order-requests/[requestId]"),
);
const SuppliesSettingsPage = lazyWithRetry(
  () => import("./pages/settings/supplies"),
);

export const appRoutes: RouteObject[] = [
  {
    path: "/",
    errorElement: <ErrorPage />,
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <IndexPage />
          </Suspense>
        ),
      },
      {
        path: "auth/sso-callback",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <SSOCallbackPage />
          </Suspense>
        ),
      },
      {
        path: "auth/sign-up",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <SignUpPage />
          </Suspense>
        ),
      },
      {
        path: "auth/sign-in",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <SignInPage />
          </Suspense>
        ),
      },
      {
        path: "auth/sign-out",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <SignOutPage />
          </Suspense>
        ),
      },
      {
        path: "auth/forgot-password",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "auth/accept-invite",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <AcceptInvitePage />
          </Suspense>
        ),
      },
      {
        path: "join-organization",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <JoinOrganizationPage />
          </Suspense>
        ),
      },
      {
        path: "accept-invitation",
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <AcceptInvitationPage />
          </Suspense>
        ),
      },
      ...(isMockEnabled()
        ? [
            {
              path: "mock-settings",
              element: (
                <Suspense fallback={<LoadingScreen />}>
                  <MockSettingsPage />
                </Suspense>
              ),
            },
          ]
        : []),
      {
        element: <AuthRoute />,
        children: [
          {
            path: "auth/onboarding",
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <OnboardingPage />
              </Suspense>
            ),
          },

          {
            path: "",
            element: (
              <BootstrapProvider>
                <Suspense fallback={<LoadingScreen />}>
                  <AppLayout />
                </Suspense>
              </BootstrapProvider>
            ),
            children: [
              {
                element: <RoleRoute />,
                children: [
                  {
                    // Home. Admins and members get the reporting
                    // dashboard, agents the personal queue - the page
                    // picks, not the router.
                    path: "dashboard",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <DashboardPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "inbox/:workspaceId",
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <InboxPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: ":contactId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <InboxPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: "reports",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <ReportsLayout />
                      </Suspense>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ReportsLandingPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "exports/:exportId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ReportExportDownloadPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "overview",
                        element: <Navigate replace to="/dashboard" />,
                      },
                      {
                        path: "conversations",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ConversationsReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "enquiries",
                        element: (
                          <Navigate
                            replace
                            to="/reports/daily-operations?tab=unanswered"
                          />
                        ),
                      },
                      {
                        path: "contacts",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ContactsReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "leads",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <LeadsReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "lost",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <LostReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sources",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <SourcesReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "broadcasts",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BroadcastsReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "lifecycle",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <LifecycleReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "follow-ups",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <FollowUpsReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "agents",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "campaigns",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CampaignsReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "messages",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <MessagesReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "daily-operations",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DailyOperationsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "daily-tracker",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DailyTrackerReportsPage />
                          </Suspense>
                        ),
                      },
                      {
                        element: <AiFeatureRoute feature={AI_FEATURE.ECHO} />,
                        children: [
                          {
                            path: "handovers",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <HandoversReportPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        element: (
                          <OrganizationTypeRoute
                            allowedType={ORGANIZATION_TYPE.ECOMMERCE}
                          />
                        ),
                        children: [
                          {
                            path: "sales",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SalesReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "sales/products",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SalesProductsReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "sales/customers",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SalesCustomersReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "sales/deliveries",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SalesDeliveriesReportsPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        element: (
                          <OrganizationTypeRoute
                            allowedType={ORGANIZATION_TYPE.CLINIC}
                          />
                        ),
                        children: [
                          {
                            path: "ai/:tab?",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <AiReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "appointments",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <AppointmentReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "attendance",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <AttendanceReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "doctors",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <DoctorsReportsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "collections/:tab?",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <CollectionsReportsPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "contacts",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <ContactsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "contacts/:contactId",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <ContactDetailPage />
                      </Suspense>
                    ),
                  },

                  {
                    path: "tickets",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <TicketsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "broadcasts",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <BroadcastsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "broadcasts/create",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <BroadcastCreatePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "broadcasts/:id",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <BroadcastDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "broadcasts/:id/edit",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <BroadcastEditPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "workflows",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <WorkflowsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "workflows/create",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <WorkflowCreatePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "workflows/:id",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <WorkflowEditPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "workflows/:id/activities",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <WorkflowActivitiesPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "ads",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <AdsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // The whole studio is Echo's: a workspace without Echo
                    // redirects to the normal fallback.
                    element: <AiFeatureRoute feature={AI_FEATURE.ECHO} />,
                    children: [
                      {
                        // Single agent per workspace → the id lives in the
                        // agent query, not the URL. The base route IS the
                        // studio (or the create form when no agent exists).
                        path: AI_ASSISTANT.slug,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentStudioPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: `${AI_ASSISTANT.slug}/quick-reply`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentQuickReplyPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: `${AI_ASSISTANT.slug}/files`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentFilesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: `${AI_ASSISTANT.slug}/tests`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentTestsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: `${AI_ASSISTANT.slug}/runs`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentRunsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: `${AI_ASSISTANT.slug}/runs/:runId`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentRunDetailPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: `${AI_ASSISTANT.slug}/availability`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentAvailabilityPage />
                          </Suspense>
                        ),
                      },
                      {
                        // One review queue for every org type - a suggestion
                        // can land in the business info, a treatment, or a
                        // doctor.
                        path: `${AI_ASSISTANT.slug}/suggestions`,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AgentSuggestionsPage />
                          </Suspense>
                        ),
                      },
                      {
                        // Clinics have no knowledge base surface: their
                        // entries are edited as "General information" in
                        // Setup and are rendered straight into the agent's
                        // prompt.
                        element: (
                          <OrganizationTypeRoute
                            excludedType={ORGANIZATION_TYPE.CLINIC}
                          />
                        ),
                        children: [
                          {
                            path: `${AI_ASSISTANT.slug}/knowledge-base`,
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <AgentKnowledgeBasePage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        // Gaps are one kind of suggestion now, not their own
                        // surface - the old deep link lands on the queue.
                        path: `${AI_ASSISTANT.slug}/knowledge-gaps`,
                        element: (
                          <Navigate replace to={assistantPath("suggestions")} />
                        ),
                      },
                      {
                        // Legacy links (id-carrying) → the new id-less route.
                        path: "ai-agents",
                        element: <Navigate replace to={assistantPath()} />,
                      },
                      {
                        path: "ai-agents/*",
                        element: <Navigate replace to={assistantPath()} />,
                      },
                    ],
                  },
                  {
                    element: (
                      <OrganizationTypeRoute
                        allowedType={ORGANIZATION_TYPE.CLINIC}
                      />
                    ),
                    children: [
                      {
                        path: "treatments",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TreatmentsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "treatments/new",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TreatmentNewPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "treatments/:treatmentId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TreatmentDetailPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "doctors",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DoctorsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "doctors/new",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DoctorNewPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "doctors/:doctorId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DoctorDetailPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "appointments",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AppointmentsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "departments",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DepartmentsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "clinics/billing",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ClinicBillingPage />
                          </Suspense>
                        ),
                      },
                      {
                        element: <SuppliesFeatureRoute />,
                        children: [
                          {
                            path: "supplies",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <SuppliesLayout />
                              </Suspense>
                            ),
                            children: [
                              {
                                index: true,
                                element: (
                                  <Navigate replace to="/supplies/inventory" />
                                ),
                              },
                              {
                                path: "inventory",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesInventoryPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "inventory/:inventoryId",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesInventoryItemPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "catalogue",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesCataloguePage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "catalogue/:productId",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesProductPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "cart",
                                element: <SuppliesCartRedirect />,
                              },
                              {
                                path: "checkout",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesCheckoutPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "orders",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesOrdersPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "orders/:orderId",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesOrderPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "order-requests",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesOrderRequestsPage />
                                  </Suspense>
                                ),
                              },
                              {
                                path: "order-requests/:requestId",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesOrderRequestPage />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "channels",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <ChannelsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "channels/create",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <CreateChannelPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "channels/manage",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <OAuthCallbackHandler />
                      </Suspense>
                    ),
                  },
                  {
                    path: "channels/manage/:id",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <ManageChannels />
                      </Suspense>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ChannelConfiguration />
                          </Suspense>
                        ),
                      },
                      {
                        path: "profile",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ChannelProfilePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "whatsapp-profile",
                        element: <ChannelRedirect to="../profile" />,
                      },
                      {
                        path: "whatsapp-templates",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WhatsappTemplatesRedirect />
                          </Suspense>
                        ),
                      },
                      {
                        path: "whatsapp-templates/create",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WhatsappTemplatesRedirect />
                          </Suspense>
                        ),
                      },
                      {
                        path: "whatsapp-templates/edit/:templateId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WhatsappTemplatesRedirect />
                          </Suspense>
                        ),
                      },
                      {
                        path: "instagram-feed",
                        element: <ChannelRedirect to="../posts" />,
                      },
                      {
                        path: "posts",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ChannelPostsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "welcome-messages",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WelcomeMessagesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "welcome-messages/create",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WelcomeMessagesEditPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "welcome-messages/edit/:messageId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WelcomeMessagesEditPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "persistent-menu",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <PersistentMenuPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    // Also the OAuth redirect URI registered with the provider. A
                    // popup relays its code and closes before React mounts, so
                    // landing here means a normal visit.
                    path: "channels/create/instagram",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <CreateInstagramChannelPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "channels/create/whatsapp",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <CreateWhatsAppChannelPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "channels/create/whatsapp-coexistence",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <CreateWhatsAppCoexistencePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "channels/create/messenger",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <CreateMessengerChannelPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Also the OAuth redirect URI registered with the provider. A
                    // popup relays its code and closes before React mounts, so
                    // landing here means a normal visit.
                    path: "channels/create/tiktok",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <CreateTikTokChannelPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "integrations",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <IntegrationsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "profile",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <ProfilePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "notifications",
                    element: (
                      <Suspense fallback={<LoadingScreen />}>
                        <NotificationsPage />
                      </Suspense>
                    ),
                  },
                  {
                    element: (
                      <OrganizationTypeRoute
                        allowedType={ORGANIZATION_TYPE.ECOMMERCE}
                      />
                    ),
                    children: [
                      {
                        path: "ecommerce",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <EcommercePage />
                          </Suspense>
                        ),
                        children: [
                          {
                            index: true,
                            element: (
                              <Navigate replace to="/ecommerce/products" />
                            ),
                          },
                          {
                            path: "products",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <ProductsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "categories",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <CategoriesPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "brands",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <BrandsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "orders",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <OrdersPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "discounts",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <DiscountsPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "variant-attributes",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <VariantAttributesPage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "store",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <StorePage />
                              </Suspense>
                            ),
                          },
                          {
                            path: "store/:storeId",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <StoreBuilderPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: "ecommerce/products/create",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ProductCreatePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "ecommerce/products/:productId/edit",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ProductEditPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "ecommerce/products/:productId/variants",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ProductVariantsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "ecommerce/products/:productId/variants/:variantId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ProductVariantsPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: "billing",
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BillingPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "success",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BillingSuccessPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "cancel",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BillingCancelPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  // Beta only while QA signs the tutorials off.
                  ...(isTutorialsEnabled()
                    ? [
                        {
                          path: "tutorials/:area?",
                          element: (
                            <Suspense fallback={<TutorialPageSkeleton />}>
                              <TutorialsPage />
                            </Suspense>
                          ),
                        },
                      ]
                    : []),
                  {
                    path: "settings",
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <SettingsOverviewPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "organization",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <OrganizationPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "snippets",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <SnippetsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "files",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <FilesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "templates",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TemplatesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "templates/new",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TemplateEditorPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "templates/:channelId/:templateId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TemplateEditorPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "users",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <UsersPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "subscription",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <SubscriptionPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "developer",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <DeveloperPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "lifecycle",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <LifecyclePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "closing-notes",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ClosingNotesPage />
                          </Suspense>
                        ),
                      },
                      {
                        element: (
                          <OrganizationTypeRoute
                            allowedType={ORGANIZATION_TYPE.CLINIC}
                          />
                        ),
                        children: [
                          {
                            path: "outcome-reasons",
                            element: (
                              <Suspense fallback={<LoadingScreen />}>
                                <OutcomeReasonsPage />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        element: (
                          <OrganizationTypeRoute
                            allowedType={ORGANIZATION_TYPE.CLINIC}
                          />
                        ),
                        children: [
                          {
                            element: <SuppliesFeatureRoute />,
                            children: [
                              {
                                path: "supplies",
                                element: (
                                  <Suspense fallback={<LoadingScreen />}>
                                    <SuppliesSettingsPage />
                                  </Suspense>
                                ),
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: "wrap-up",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WrapUpPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "ai",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AiSettingsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "credits",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <CreditsSettingsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "inbox",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <InboxSettingsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "sla",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <SlaSettingsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "assignment",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <AssignmentPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "tags",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TagsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "contact-fields",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <ContactFieldsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "websites",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WebsitesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "workspace",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WorkspaceSettingsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "workspace-users",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <WorkspaceUsersPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "teams",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <TeamsPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "roles",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <RolesPage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "roles/:roleId",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <RolePage />
                          </Suspense>
                        ),
                      },
                      {
                        path: "business-hours",
                        element: (
                          <Suspense fallback={<LoadingScreen />}>
                            <BusinessHoursPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// Create router as a singleton to prevent multiple instances during HMR
let routerInstance: ReturnType<typeof createBrowserRouter> | null = null;

export const router = (() => {
  if (!routerInstance) {
    routerInstance = createBrowserRouter(appRoutes);
  }

  return routerInstance;
})();
